/**
 * Phase 7 Slice B — shared period selector with custom date range.
 *
 * Renders the 4 presets (7d/30d/90d/365d) inline + a "Custom" popover with
 * a from/to date picker. When custom range is active, the pill shows the
 * formatted range and `value` becomes `{ since, until, mode: "custom" }`.
 *
 * Used by Cockpit, CampaignCockpitDetail, and AdSpend. Centralizing keeps
 * the wire-format (`since/until` ISO timestamps) consistent across pages.
 */
import React, { useMemo, useState } from "react";
import { Calendar as CalendarIcon } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { useT } from "../../lib/i18n";

const PRESETS = [
  { key: "7d", days: 7 },
  { key: "30d", days: 30 },
  { key: "90d", days: 90 },
  { key: "365d", days: 365 },
];

function isoDaysAgo(days) {
  const d = new Date(); d.setDate(d.getDate() - days);
  return d.toISOString();
}
function todayIso() { return new Date().toISOString(); }
function startOfDayIso(d) {
  const x = new Date(d); x.setHours(0, 0, 0, 0); return x.toISOString();
}
function endOfDayIso(d) {
  const x = new Date(d); x.setHours(23, 59, 59, 999); return x.toISOString();
}
function fmtDateLocal(d, locale) {
  if (!d) return "";
  return new Intl.DateTimeFormat(locale === "de" ? "de-DE" : "en-GB",
        { day: "2-digit", month: "2-digit", year: "numeric" })
      .format(d instanceof Date ? d : new Date(d));
}

/**
 * Computes the `{ since, until }` ISO pair for a preset key.
 * Exported so pages that need the initial value can compute it without
 * mounting the component (e.g. tanstack-query keys).
 */
export function rangeForPreset(presetKey) {
  const p = PRESETS.find((x) => x.key === presetKey) || PRESETS[1];
  return { since: isoDaysAgo(p.days), until: todayIso() };
}

export default function PeriodSelector({ value, onChange, locale = "de" }) {
  const t = useT();
  const [open, setOpen] = useState(false);
  const initFrom = value?.mode === "custom" && value?.since
    ? value.since.slice(0, 10) : "";
  const initTo = value?.mode === "custom" && value?.until
    ? value.until.slice(0, 10) : "";
  const [from, setFrom] = useState(initFrom);
  const [to, setTo] = useState(initTo);

  // Live validation — drives both the inline error and the Apply disabled state.
  const validation = useMemo(() => {
    if (!from || !to) {
      return { valid: false,
                  error: null,    // empty fields: not an error, just incomplete
                  reason: "incomplete" };
    }
    const fromD = new Date(from + "T00:00:00");
    const toD = new Date(to + "T23:59:59");
    if (toD < fromD) {
      return { valid: false,
                  error: t("period.error_order_strict",
                            "Das Enddatum muss nach dem Startdatum liegen."),
                  reason: "order" };
    }
    const today = new Date();
    if (toD > today) {
      return { valid: false,
                  error: t("period.error_future",
                            "Enddatum darf nicht in der Zukunft liegen."),
                  reason: "future" };
    }
    return { valid: true, error: null, reason: null };
  }, [from, to, t]);

  const apply = () => {
    if (!validation.valid) return;
    const fromD = new Date(from + "T00:00:00");
    const toD = new Date(to + "T23:59:59");
    onChange({
      mode: "custom",
      since: startOfDayIso(fromD),
      until: endOfDayIso(toD),
      preset: null,
    });
    setOpen(false);
  };

  const isCustom = value?.mode === "custom";
  const customLabel = isCustom && value.since && value.until
    ? `${fmtDateLocal(value.since, locale)} – ${fmtDateLocal(value.until, locale)}`
    : t("period.custom", "Custom");

  return (
    <div className="flex flex-wrap gap-1 items-center"
         data-testid="period-selector">
      {PRESETS.map((p) => (
        <Button key={p.key} size="sm"
                variant={(!isCustom && value?.preset === p.key)
                          ? "default" : "outline"}
                onClick={() => onChange({
                  mode: "preset", preset: p.key,
                  ...rangeForPreset(p.key),
                })}
                data-testid={`period-${p.key}`}>
          {t(`cockpit.period_${p.key}`, p.key)}
        </Button>
      ))}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button size="sm" variant={isCustom ? "default" : "outline"}
                  data-testid="period-custom">
            <CalendarIcon className="h-4 w-4 mr-1" />
            {customLabel}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-72 p-3" align="start"
                          data-testid="period-custom-popover">
          <div className="text-xs font-medium mb-3"
               data-testid="period-custom-title">
            {t("period.custom_title", "Eigener Zeitraum wählen")}
          </div>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">{t("period.from", "Von")}</Label>
              <Input type="date" value={from}
                     onChange={(e) => setFrom(e.target.value)}
                     data-testid="period-custom-from-input" />
            </div>
            <div>
              <Label className="text-xs">{t("period.to", "Bis")}</Label>
              <Input type="date" value={to}
                     onChange={(e) => setTo(e.target.value)}
                     data-testid="period-custom-to-input" />
            </div>
          </div>
          {validation.error && (
            <div className="text-xs text-rose-600 mt-2"
                 role="alert"
                 data-testid="period-custom-error">
              {validation.error}
            </div>
          )}
          <div className="flex justify-end gap-2 mt-3">
            <Button size="sm" variant="ghost" onClick={() => setOpen(false)}
                    data-testid="period-custom-cancel">
              {t("common.cancel", "Abbrechen")}
            </Button>
            <Button size="sm" onClick={apply}
                    disabled={!validation.valid}
                    data-testid="period-custom-apply">
              {t("period.apply", "Anwenden")}
            </Button>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
